import React, { useState, useEffect, useRef } from 'react';
import { Card, Spinner, Alert, Button, Row, Col } from 'react-bootstrap';
import { FaSearch, FaExternalLinkAlt } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import SearchViewApi from '../../api/SearchViewApi';
import ObjectConfigApi from '../../api/ObjectConfigApi';
import DataApi from '../../api/DataApi';
import { formatDate, formatDateTime, formatTime, formatFieldValue as formatFieldValueUtil } from '../../utils/formatUtils';
import { theme } from '../../theme/theme.js';

/**
 * SearchWidget Component
 * Displays search results based on a search view configuration
 * Supports field bindings from the detail record to search parameters
 */
const SearchWidget = ({
  widget,
  currentRecordId,
  sourceObjectName,
  recordData
}) => {
  const navigate = useNavigate();
  const [searchViewConfig, setSearchViewConfig] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldDataTypes, setFieldDataTypes] = useState({});
  const [fieldMetadata, setFieldMetadata] = useState({});
  const abortControllerRef = useRef(null);

  const widgetConfig = widget?.config || {};
  const searchViewId = widgetConfig.search_view_id;
  const filterBindings = widgetConfig.filter_bindings || []; // [{ filterable_field, source_field }]
  const selectedFields = widgetConfig.fields || []; // Fields selected to display
  const maxRecords = widgetConfig.max_records || 10;
  const maxHeight = widgetConfig.max_height || 400;

  // Get search object name from config (will be set after searchViewConfig loads)
  const searchObjectName = searchViewConfig?.object_name;

  useEffect(() => {
    if (searchViewId) {
      fetchSearchViewConfig();
    }

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchViewId]);

  // Auto-execute search when config and record data are available
  useEffect(() => {
    if (searchViewConfig && recordData && searchObjectName && Object.keys(fieldDataTypes).length > 0) {
      // Execute search even if no filter bindings (will show all records)
      executeSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchViewConfig, recordData, filterBindings, fieldDataTypes, searchObjectName]);

  const fetchSearchViewConfig = async () => {
    if (!searchViewId) return;

    try {
      const searchViewApi = new SearchViewApi();
      const response = await searchViewApi.fetchById(searchViewId);
      console.log('SearchWidget - searchViewConfig response:', response);
      if (response?.data?.success && response.data.data) {
        const config = response.data.data;
        setSearchViewConfig(config);
        // Fetch object config for the search view's object
        if (config.object_name) {
          fetchObjectConfig(config.object_name);
        }
      } else {
        setError(`Search view not found.`);
      }
    } catch (err) {
      console.error('Error fetching search view config:', err);
      setError(err.response?.data?.message || 'Failed to load search view configuration');
    }
  };

  const fetchObjectConfig = async (objectName) => {
    if (!objectName) return;

    try {
      const response = await ObjectConfigApi.fetchConfigObjectByNameWithFields(objectName);
      const result = response.data;

      if (result?.fields?.length > 0) {
        const dataTypes = {};
        const metadata = {};

        result.fields.forEach(field => {
          const fieldName = field.field_name;
          dataTypes[fieldName] = field.data_type || 'text';

          metadata[fieldName] = {
            data_type: field.data_type || 'text',
            picklist_values: field.picklist_values_data || field.picklist_values || [],
            reference_object: field.reference_object,
            display_fields: field.display_fields,
            lookup_search_fields: field.lookup_search_fields || 'name',
            field_label: field.field_label || fieldName
          };
        });

        setFieldDataTypes(dataTypes);
        setFieldMetadata(metadata);
      }
    } catch (err) {
      console.error('Error fetching object config:', err);
    }
  };

  const executeSearch = async () => {
    if (!searchViewConfig || !searchObjectName || !recordData) return;

    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setLoading(true);
    setError('');

    try {
      const dataApi = new DataApi(`object/list/${searchObjectName}`);
      const queryParams = new URLSearchParams();

      // Parse display_fields if it's a JSON string
      let displayFieldsArray = [];
      if (Array.isArray(searchViewConfig.display_fields)) {
        displayFieldsArray = searchViewConfig.display_fields;
      } else if (typeof searchViewConfig.display_fields === 'string') {
        try {
          displayFieldsArray = JSON.parse(searchViewConfig.display_fields);
        } catch (e) {
          // If not JSON, try splitting by comma
          displayFieldsArray = searchViewConfig.display_fields.split(',').map(f => f.trim()).filter(f => f);
        }
      }

      // Add fields to fetch - use selected fields if available, otherwise use all display_fields
      // Always include 'id' field for navigation
      const fieldsToFetch = selectedFields.length > 0
        ? selectedFields
        : displayFieldsArray;

      // Ensure 'id' is included in fields to fetch
      const fieldsWithId = fieldsToFetch.includes('id')
        ? fieldsToFetch
        : ['id', ...fieldsToFetch];

      if (fieldsWithId.length > 0) {
        queryParams.append('fields', fieldsWithId.join(','));
      }

      // Build filters from filter_bindings
      // filter_bindings format: [{ filterable_field, source_field }]
      if (filterBindings.length > 0) {
        let filterIndex = 1;
        filterBindings.forEach((binding) => {
          const { filterable_field, source_field } = binding;

          if (!filterable_field || !source_field) return;

          // Get value from source record using the source_field
          const sourceValue = getFieldValue(recordData, source_field);

          if (sourceValue !== null && sourceValue !== undefined && sourceValue !== '') {
            // Use filterable_field as the target field for the search
            // Default operator is 'equals'
            queryParams.append(`filter${filterIndex}`, `${filterable_field}:equals:${sourceValue}`);
            filterIndex++;
          }
        });

        if (filterIndex > 1) {
          queryParams.append('logic', 'AND');
        }
      }

      // Add sorting
      if (searchViewConfig.sort_by) {
        queryParams.append('sortBy', searchViewConfig.sort_by);
        queryParams.append('sortOrder', searchViewConfig.sort_order || 'DESC');
      }

      // Limit results
      queryParams.append('limit', maxRecords.toString());

      const response = await dataApi.fetchAll(queryParams.toString(), {
        signal: abortController.signal
      });

      if (abortController.signal.aborted) return;

      if (response?.data?.success) {
        setRecords(response.data.data || []);
      } else {
        setError(response?.data?.message || 'Failed to fetch records');
      }
    } catch (err) {
      if (err.name === 'AbortError' || err.name === 'CanceledError') {
        return;
      }
      console.error('Error fetching records:', err);
      setError(err.response?.data?.message || 'Failed to fetch records');
    } finally {
      if (!abortController.signal.aborted) {
        setLoading(false);
      }
    }
  };

  const getFieldValue = (record, fieldName) => {
    if (!record || !fieldName) return null;

    // Normalize field name (case-insensitive)
    const normalizedFieldName = String(fieldName).toLowerCase();

    // Search in highlightSection fields
    if (Array.isArray(record.highlightSection)) {
      const highlightField = record.highlightSection.find(
        f => f?.fieldName?.toLowerCase() === normalizedFieldName
      );
      if (highlightField && highlightField.value !== null && highlightField.value !== undefined) {
        return highlightField.value;
      }
    }

    // Search in details sections
    if (Array.isArray(record.details)) {
      for (const section of record.details) {
        if (Array.isArray(section.fields)) {
          const detailField = section.fields.find(
            f => f?.fieldName?.toLowerCase() === normalizedFieldName
          );
          if (detailField && detailField.value !== null && detailField.value !== undefined) {
            return detailField.value;
          }
        }
      }
    }

    // Search in otherHiddenFields
    if (Array.isArray(record.otherHiddenFields)) {
      const hiddenField = record.otherHiddenFields.find(
        f => f?.fieldName?.toLowerCase() === normalizedFieldName
      );
      if (hiddenField && hiddenField.value !== null && hiddenField.value !== undefined) {
        return hiddenField.value;
      }
    }

    // Fallback: try direct property access (for nested objects)
    if (record[fieldName] !== undefined) {
      const value = record[fieldName];
      // Handle lookup fields - extract ID if it's an object
      if (value && typeof value === 'object' && value.id) {
        return value.id;
      }
      return value;
    }

    return null;
  };

  const extractPrimitiveValue = (rawValue) => {
    if (rawValue === null || rawValue === undefined) return null;
    if (typeof rawValue !== 'object' || Array.isArray(rawValue)) return rawValue;
    if (rawValue.value !== undefined) {
      const extracted = extractPrimitiveValue(rawValue.value);
      if (extracted !== null) return extracted;
    }
    if (rawValue.displayValue !== undefined) {
      const extracted = extractPrimitiveValue(rawValue.displayValue);
      if (extracted !== null) return extracted;
    }
    if (rawValue.name !== undefined) {
      const extracted = extractPrimitiveValue(rawValue.name);
      if (extracted !== null) return extracted;
    }
    try {
      const stringValue = String(rawValue);
      return stringValue === '[object Object]' ? null : stringValue;
    } catch {
      return null;
    }
  };

  const formatFieldValue = (rawValue, fieldName) => {
    if (rawValue === null || rawValue === undefined || rawValue === '') {
      return '—';
    }

    const metadata = fieldMetadata[fieldName] || {};
    const dataType = (metadata.data_type || fieldDataTypes[fieldName] || 'text').toLowerCase();
    const displayValue = typeof rawValue === 'object' && rawValue !== null
      ? extractPrimitiveValue(rawValue.displayValue)
      : null;
    const primitiveValue = extractPrimitiveValue(rawValue);

    // Handle lookup/reference fields
    if (['lookup', 'reference'].includes(dataType)) {
      return displayValue || primitiveValue || '—';
    }

    // Handle picklist fields
    if (dataType === 'picklist' || dataType === 'multipicklist') {
      return displayValue || primitiveValue || '—';
    }

    // Handle date/time fields
    if (dataType === 'date') {
      return primitiveValue ? formatDate(primitiveValue) : '—';
    } else if (dataType === 'datetime') {
      return primitiveValue ? formatDateTime(primitiveValue) : '—';
    } else if (dataType === 'time') {
      return primitiveValue ? formatTime(primitiveValue) : '—';
    }

    // Handle boolean fields
    if (dataType === 'boolean' || dataType === 'checkbox') {
      return primitiveValue ? 'Yes' : 'No';
    }

    const formatted = formatFieldValueUtil({
      fieldName,
      label: metadata.field_label || fieldName,
      type: metadata.data_type || dataType,
      value: primitiveValue,
      displayValue: typeof rawValue === 'object' && rawValue !== null ? rawValue.displayValue : null,
      scale: rawValue?.scale ?? metadata.scale,
      precision: rawValue?.precision ?? metadata.precision,
      displayConfig: rawValue?.displayConfig ?? metadata.displayConfig
    });

    if (formatted !== null && formatted !== undefined && formatted !== '') {
      return formatted;
    }

    return primitiveValue !== null && primitiveValue !== undefined && primitiveValue !== ''
      ? String(primitiveValue)
      : '—';
  };

  const handleRowClick = (record) => {
    // Get record ID - handle both direct value and object with value property
    let recordId = null;
    if (record.id) {
      if (typeof record.id === 'object' && record.id.value) {
        recordId = record.id.value;
      } else if (typeof record.id === 'string') {
        recordId = record.id;
      }
    }

    if (recordId && searchObjectName) {
      navigate(`/view/${searchObjectName}/${recordId}`);
    }
  };

  // Helper to get record ID value
  const getRecordIdValue = (record) => {
    if (!record) return null;
    if (record.id && typeof record.id === 'string') {
      return record.id;
    }
    if (record.id && typeof record.id === 'object' && record.id.value) {
      return record.id.value;
    }
    return record.id || null;
  };

  const handleViewAll = () => {
    // Navigate to full search page with pre-filled filters
    const params = new URLSearchParams();
    let filterIndex = 1;
    filterBindings.forEach((binding) => {
      if (!binding.filterable_field || !binding.source_field) return;
      const sourceValue = getFieldValue(recordData, binding.source_field);
      if (sourceValue !== null && sourceValue !== undefined && sourceValue !== '') {
        params.append(`filter${filterIndex}`, `${binding.filterable_field}:equals:${sourceValue}`);
        filterIndex++;
      }
    });
    if (filterIndex > 1) {
      params.append('logic', 'AND');
    }
    navigate(`/search/${searchObjectName}?${params.toString()}`);
  };

  if (!searchViewId || !searchViewConfig) {
    return (
      <Card className="mb-3 widget-card">
        <Card.Header className="widget-card-header">
          <h5 className="mb-0">{widget?.widgetLabel || 'Search Widget'}</h5>
        </Card.Header>
        <Card.Body>
          <Alert variant="warning">Search view not configured for this widget.</Alert>
        </Card.Body>
      </Card>
    );
  }

  if (!searchObjectName) {
    return (
      <Card className="mb-3 widget-card">
        <Card.Header className="widget-card-header">
          <h5 className="mb-0">{widget?.widgetLabel || 'Search Widget'}</h5>
        </Card.Header>
        <Card.Body>
          <Alert variant="warning">Search view object not found.</Alert>
        </Card.Body>
      </Card>
    );
  }

  if (error && !loading) {
    return (
      <Card className="mb-3 widget-card">
        <Card.Header className="widget-card-header">
          <h5 className="mb-0">{widget?.widgetLabel || 'Search Widget'}</h5>
        </Card.Header>
        <Card.Body>
          <Alert variant="danger">{error}</Alert>
        </Card.Body>
      </Card>
    );
  }

  // Parse display_fields if it's a JSON string
  let displayFieldsArray = [];
  if (Array.isArray(searchViewConfig?.display_fields)) {
    displayFieldsArray = searchViewConfig.display_fields;
  } else if (typeof searchViewConfig?.display_fields === 'string') {
    try {
      displayFieldsArray = JSON.parse(searchViewConfig.display_fields);
    } catch (e) {
      // If not JSON, try splitting by comma
      displayFieldsArray = searchViewConfig.display_fields.split(',').map(f => f.trim()).filter(f => f);
    }
  }

  // Use selected fields if available, otherwise use all display_fields from search view config
  // Filter out 'id' from display fields (it's used for navigation but not displayed)
  const displayFields = (selectedFields.length > 0
    ? selectedFields
    : displayFieldsArray).filter(fieldName => fieldName !== 'id');

  return (
    <Card className="mb-3 widget-card" style={{ width: '100%' }}>
      <Card.Header
        className="widget-card-header"
        style={{
          padding: '12px 16px',
          borderBottom: '1px solid #dee2e6',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#0176d3',
          color: 'rgb(255, 255, 255)'
        }}
      >
        <h5
          className="mb-0"
          style={{
            fontFamily: theme.typography.fontFamily.primary,
            fontWeight: theme.typography.fontWeight.semibold,
            fontSize: theme.typography.fontSize.lg,
            color: 'rgb(255, 255, 255)'
          }}
        >
          {widget?.widgetLabel || 'Search Results'}
        </h5>
        {records.length > 0 && (
          <Button
            variant="link"
            size="sm"
            onClick={handleViewAll}
            style={{ color: 'white', padding: '0 8px', textDecoration: 'none' }}
          >
            View All <FaExternalLinkAlt className="ms-1" />
          </Button>
        )}
      </Card.Header>
      <Card.Body style={{ padding: '1rem', maxHeight: `${maxHeight}px`, overflowY: 'auto' }}>
        {loading ? (
          <div className="text-center py-3">
            <Spinner animation="border" size="sm" />
            <p className="mt-2 mb-0" style={{ fontSize: '0.875rem' }}>Loading search results...</p>
          </div>
        ) : records.length === 0 ? (
          <div className="text-center py-3">
            <FaSearch style={{ fontSize: '2rem', color: '#ccc', marginBottom: '0.5rem' }} />
            <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>No records found</p>
          </div>
        ) : (
          <div className="widget-records" style={{ width: '100%' }}>
            {records.map((record, idx) => {
              const recordId = getRecordIdValue(record);

              return (
                <Card
                  key={recordId || idx}
                  className="mb-2 widget-record-card"
                  style={{
                    border: '1px solid #e0e0e0',
                    width: '100%',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    ':hover': {
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }
                  }}
                  onClick={() => handleRowClick(record)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
                    e.currentTarget.style.borderColor = theme.colors.primary;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.borderColor = '#e0e0e0';
                  }}
                >
                  <Card.Body className="p-3">
                    <Row>
                      {displayFields && displayFields.length > 0 ? (
                        displayFields.map((fieldName, fieldIdx) => {
                          const fieldValue = record[fieldName];

                          const fieldLabel = fieldMetadata[fieldName]?.field_label || fieldName;

                          return (
                            <Col xs={12} md={6} key={fieldIdx} className="mb-2">
                              <div style={{ marginBottom: '4px' }}>
                                <div
                                  style={{
                                    fontFamily: theme.typography.fontFamily.primary,
                                    fontWeight: theme.typography.fontWeight.medium,
                                    color: theme.colors.textSecondary,
                                    lineHeight: theme.typography.lineHeight.normal,
                                    fontSize: theme.typography.fontSize.lg,
                                    marginBottom: '2px'
                                  }}
                                >
                                  {fieldLabel}
                                </div>
                                <div
                                  style={{
                                    fontFamily: theme.typography.fontFamily.primary,
                                    fontWeight: theme.typography.fontWeight.normal,
                                    color: theme.colors.textPrimary,
                                    lineHeight: theme.typography.lineHeight.normal,
                                    fontSize: theme.typography.fontSize.lg,
                                    wordBreak: 'break-word'
                                  }}
                                >
                                  {formatFieldValue(fieldValue, fieldName)}
                                </div>
                              </div>
                            </Col>
                          );
                        })
                      ) : (
                        <Col xs={12}>
                          <p className="text-muted" style={{ fontSize: theme.typography.fontSize.lg }}>
                            No fields configured for this widget
                          </p>
                        </Col>
                      )}
                    </Row>
                  </Card.Body>
                </Card>
              );
            })}
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default SearchWidget;
